/**
 * Roadmap Service
 *
 * Domain layer for the Roadmap (Ideas) screen. Datasource-agnostic: all
 * Firestore access is delegated to firestoreService. This service bridges
 * the data layer to entities — it fetches raw roadmap JSON, remaps it to
 * RoadmapItemEntity (via RoadmapItemRemapper.fromJsonList → DTO → entity),
 * and fills each item's `likeEntity` from the locally-stored like state.
 *
 * Singleton pattern for consistent state and efficient resource usage —
 * mirrors the other domain service singletons.
 *
 * Flutter parity:
 * - lib/presentation/screens/roadmap/roadmap_screen.dart:402-445 (_getData):
 *   choose getRoadmap(...) vs getNotificationRoadmapItem(...) based on
 *   `itemInfo == null`; for each item `toEntity()` then attach the local
 *   like entity if present.
 * - lib/presentation/screens/roadmap/roadmap_screen.dart:396-400
 *   (_getLocalRoadmapItemLikeEntity): reads the like-state box keyed by
 *   `RoadmapItemLikeEntity.keyName + roadmapItemId`. The web equivalent is a
 *   single-key SecureStorage read under `SECURE_STORAGE_KEYS.ROADMAP_LIKES`
 *   (`RoadmapItemLike_` + roadmapItemId), the same prefix
 *   syncSecureStorageUseCase.syncRoadmapLikes writes to.
 *
 * The like *action* and its reactive local-DB watch are NOT ported here —
 * only the like *state* fill on fetch (story Context, task-prompt point 4).
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { RoadmapItemRemapper } from '@/domain/remappers/roadmapItem/roadmapItemRemapper';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { RoadmapItemLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { RoadmapItemDto } from '@/data/dtos/roadmapItem/roadmapItemDto';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { RoadmapItemAuthor } from '@/domain/input/roadmap/roadmapItemAuthor';

export interface RoadmapPage {
  items: RoadmapItemEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class RoadmapService {
  private static instance: RoadmapService;

  private constructor() { }

  public static getInstance(): RoadmapService {
    if (!RoadmapService.instance) {
      RoadmapService.instance = new RoadmapService();
    }
    return RoadmapService.instance;
  }

  /**
   * Paginated fetch of roadmap items.
   *
   * Mirrors Flutter's `itemInfo == null` branch of _getData
   * (roadmap_screen.dart:419-422): RoadmapRepository().getRoadmap(...).
   * Each DTO is mapped to an entity (Flutter `item.toEntity()`) and its
   * local like-state is attached when present.
   */
  async fetchRoadmap(limit: number, cursor?: PageCursor): Promise<RoadmapPage> {
    const page = await firestoreService.getRoadmap(limit, cursor);
    const dtos = RoadmapItemRemapper.fromJsonList(page.items);
    const items = await Promise.all(dtos.map((dto) => this.toEntityWithLikeState(dto)));
    return {
      items,
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }

  /**
   * Fetch a single roadmap item opened from a notification, returned as a
   * one-element list to match Flutter's single-item branch of _getData
   * (roadmap_screen.dart:423-425, which returns a RoadmapResponse whose
   * `items` holds the single item). Empty when the item does not exist.
   */
  async fetchNotificationRoadmapItem(roadmapItemId: string): Promise<RoadmapItemEntity[]> {
    const page = await firestoreService.getNotificationRoadmapItem(roadmapItemId);
    const dtos = RoadmapItemRemapper.fromJsonList(page.items);
    return Promise.all(dtos.map((dto) => this.toEntityWithLikeState(dto)));
  }

  /**
   * Build and submit a new roadmap item, returning the Cloud Function's
   * success boolean.
   *
   * Mirrors Flutter's `_submitRoadmapItem`
   * (add_roadmap_item_screen.dart:82-108): construct a `RoadmapItem` with the
   * exact field values below, then `RoadmapRepository().addRoadmapItem(item)`
   * → `CloudFunctionsService.addRoadmapItem` (cloud_functions_service.dart:
   * 995-1010), which posts `{ item: item.toJson() }`.
   *
   * Field values (add_roadmap_item_screen.dart:93-106):
   * - `id`              = `Uuid().v1()`                → `generateUuidV1()`
   * - `title`           = title field
   * - `description`     = description field
   * - `createdTimestamp`= `DateTime.now().millisecondsSinceEpoch` → `Date.now()`
   * - `order`           = `-1`
   * - `status`          = `0`
   * - `userId`          = `currentUser.firebaseId`
   * - `userName`        = `currentUser.name`
   * - `likes`/`dislikes`/`comments` = `0`
   * - `usd`             = `0`
   *
   * `dueTimestamp` is never assigned in the Flutter builder, but Flutter's
   * serializer is unconditional (`_$RoadmapItemToJson`, roadmap_item.g.dart:
   * 24-39, line 30 always writes `'dueTimestamp': instance.dueTimestamp`), so
   * the `{ item }` wire payload carries `"dueTimestamp": null` — the key is
   * present with a null value. We therefore set `dueTimestamp: null`
   * explicitly so the serialized payload matches Flutter's 13-key field set
   * byte-for-byte (left `undefined`, JSON serialization would drop the key —
   * a Cloud-Function-payload mismatch). `RoadmapItemDto.dueTimestamp` is
   * `number | null`, so the literal is a plain DTO with no cast.
   */
  async addRoadmapItem(
    title: string,
    description: string,
    currentUser: RoadmapItemAuthor,
  ): Promise<boolean> {
    const item: RoadmapItemDto = {
      id: generateUuidV1(),
      title,
      description,
      createdTimestamp: Date.now(),
      dueTimestamp: null,
      order: -1,
      status: 0,
      userId: currentUser.firebaseId,
      userName: currentUser.name,
      likes: 0,
      dislikes: 0,
      comments: 0,
      usd: 0,
    };

    return cloudFunctionsService.addRoadmapItem(item);
  }

  /**
   * Fetch the current user's most recently created roadmap item as an entity,
   * or `null` when the user has none.
   *
   * Mirrors Flutter's post-add reload in `roadmap_screen.dart:145-147`:
   * `RoadmapRepository().getLatestUserRoadmapItem(userId)` then `.toEntity()`.
   * The raw JSON is bridged via `RoadmapItemRemapper.fromJson` (DTO) and
   * reuses `toEntityWithLikeState` so the inserted item carries the same
   * like-state fill as paged items.
   */
  async fetchLatestUserRoadmapItem(userId: string): Promise<RoadmapItemEntity | null> {
    const json = await firestoreService.getLatestUserRoadmapItem(userId);
    if (!json) {
      return null;
    }
    const dto = RoadmapItemRemapper.fromJson(json);
    return this.toEntityWithLikeState(dto);
  }

  /**
   * Map a RoadmapItemDto to a RoadmapItemEntity (straight field copy —
   * Flutter `RoadmapItem.toEntity`, roadmap_item.dart:28-43, with
   * `isSendingLike` defaulting to false) and fill `likeEntity` from local
   * like-state.
   */
  private async toEntityWithLikeState(dto: RoadmapItemDto): Promise<RoadmapItemEntity> {
    const entity: RoadmapItemEntity = {
      id: dto.id,
      title: dto.title,
      description: dto.description,
      createdTimestamp: dto.createdTimestamp,
      dueTimestamp: dto.dueTimestamp,
      order: dto.order,
      status: dto.status,
      userId: dto.userId,
      userName: dto.userName,
      likes: dto.likes,
      dislikes: dto.dislikes,
      comments: dto.comments,
      usd: dto.usd,
      isSendingLike: false,
    };

    await this.fillLikeState(entity);
    return entity;
  }

  /**
   * Read the locally-stored like-state for this item and, when present,
   * set `entity.likeEntity`. Mirrors Flutter's _getLocalRoadmapItemLikeEntity
   * (roadmap_screen.dart:396-400): a single-key read under the
   * `RoadmapItemLike_` + roadmapItemId key, attaching the result only when
   * a record exists (Flutter leaves it null otherwise → icons render
   * uncolored).
   *
   * A read failure must not drop the item: it is logged and swallowed,
   * leaving `likeEntity` undefined.
   */
  private async fillLikeState(entity: RoadmapItemEntity): Promise<void> {
    try {
      const storage = SecureStorageWebCrypto.getInstance();
      const record = await storage.getEncrypted<RoadmapItemLikeStorage>(
        SECURE_STORAGE_KEYS.ROADMAP_LIKES + entity.id,
      );
      if (record) {
        entity.likeEntity = {
          roadmapItemId: entity.id,
          liked: record.liked,
          disliked: record.disliked,
        };
      }
    } catch (error) {
      appLogger.error(
        'Error reading roadmap item like-state',
        'RoadmapService.fillLikeState',
        { error: error as Error },
      );
    }
  }
}

// Export singleton instance for convenience
export const roadmapService = RoadmapService.getInstance();
